import { useEffect, useMemo, useState } from 'react';
import type { CreatorProfile } from '@/types/portal';
import { api, apiList } from '@/utils/api';

export type CreatorTier = 'nano' | 'micro' | 'mid' | 'macro' | 'mega';

/** Follower tiers as Laravel's Discovery defines them (InfluencerDiscoveryController::TIER_RANGES). */
export const FOLLOWER_TIERS: { id: CreatorTier; label: string; hint: string }[] = [
  { id: 'nano', label: 'Nano', hint: '1K – 10K' },
  { id: 'micro', label: 'Micro', hint: '10K – 50K' },
  { id: 'mid', label: 'Mid', hint: '50K – 500K' },
  { id: 'macro', label: 'Macro', hint: '500K – 1M' },
  { id: 'mega', label: 'Mega', hint: '1M+' },
];

export function getTier(followers: number): CreatorTier {
  if (followers < 10000) return 'nano';
  if (followers < 50000) return 'micro';
  if (followers < 500000) return 'mid';
  if (followers < 1000000) return 'macro';
  return 'mega';
}

/** Filter values the rail offers, from `GET /discovery/options`. */
export interface DiscoveryOptions {
  categories: string[];
  platforms: string[];
  markets: string[];
}

export interface DiscoveryFilters {
  categories: string[];
  markets: string[];
  platforms: string[];
  tiers: CreatorTier[];
  minEngagement: number;
}

const emptyFilters: DiscoveryFilters = {
  categories: [],
  markets: [],
  platforms: [],
  tiers: [],
  minEngagement: 0,
};

export interface FilterChip {
  id: string;
  label: string;
  remove: () => void;
}

export type SortKey = 'fit' | 'followers' | 'engagement' | 'rate';

const keywordCategory: { keywords: string[]; category: string }[] = [
  { keywords: ['skincare', 'beauty', 'cosmetic', 'glow'], category: 'Beauty & Skincare' },
  { keywords: ['fintech', 'finance', 'money', 'wallet', 'payment', 'banking'], category: 'Tech & Fintech' },
  { keywords: ['food', 'recipe', 'grocery', 'cook', 'kitchen'], category: 'Food & Recipes' },
  { keywords: ['fashion', 'style', 'outfit'], category: 'Fashion' },
  { keywords: ['fitness', 'wellness', 'health', 'workout'], category: 'Fitness & Wellness' },
  { keywords: ['parent', 'mother', 'mama', 'baby', 'family'], category: 'Parenting' },
  { keywords: ['travel', 'destination', 'tour'], category: 'Travel' },
  { keywords: ['home', 'interior', 'decor'], category: 'Home & Interiors' },
  { keywords: ['music', 'festival', 'entertainment', 'nightlife'], category: 'Music & Entertainment' },
  { keywords: ['sport', 'football', 'match'], category: 'Sports' },
  { keywords: ['heritage', 'culture', 'tradition'], category: 'Heritage & Culture' },
  { keywords: ['lifestyle'], category: 'Lifestyle' },
];

const keywordMarket: { keywords: string[]; market: string }[] = [
  { keywords: ['kenya', 'nairobi', 'mombasa'], market: 'Kenya' },
  { keywords: ['uganda', 'kampala'], market: 'Uganda' },
  { keywords: ['tanzania', 'dar', 'dares', 'dodoma'], market: 'Tanzania' },
  { keywords: ['congo', 'kinshasa', 'drc'], market: 'DR Congo' },
  { keywords: ['ethiopia', 'addis'], market: 'Ethiopia' },
];

const keywordPlatform: { keywords: string[]; platform: string }[] = [
  { keywords: ['tiktok'], platform: 'TikTok' },
  { keywords: ['instagram', 'ig', 'reels'], platform: 'Instagram' },
  { keywords: ['youtube', 'long-form'], platform: 'YouTube' },
  { keywords: ['twitter', ' x '], platform: 'X' },
  { keywords: ['facebook'], platform: 'Facebook' },
];

/**
 * Keyword parsing of a brief into filters, used when the AI parse
 * (POST /discovery/parse, Laravel's DiscoveryAiService) isn't available.
 * Categories are matched against the real category names.
 */
function inferFilters(prompt: string, availableCategories: string[]): Partial<DiscoveryFilters> {
  const text = ` ${prompt.toLowerCase()} `;
  const inferred: Partial<DiscoveryFilters> = {};

  const topics = keywordCategory
    .filter((entry) => entry.keywords.some((k) => text.includes(k)))
    .map((entry) => entry.category.toLowerCase());
  const categories = availableCategories.filter((name) => {
    const lower = name.toLowerCase();
    return text.includes(lower) || topics.some((topic) => topic.includes(lower) || lower.includes(topic.split(' ')[0]));
  });
  if (categories.length) inferred.categories = Array.from(new Set(categories));

  const markets = keywordMarket
    .filter((entry) => entry.keywords.some((k) => text.includes(k)))
    .map((entry) => entry.market);
  if (markets.length) inferred.markets = Array.from(new Set(markets));

  const platforms = keywordPlatform
    .filter((entry) => entry.keywords.some((k) => text.includes(k)))
    .map((entry) => entry.platform);
  if (platforms.length) inferred.platforms = Array.from(new Set(platforms));

  const engagementMatch = text.match(/(\d+(?:\.\d+)?)\s*%/);
  if (engagementMatch) {
    inferred.minEngagement = Number.parseFloat(engagementMatch[1]);
  }

  if (text.includes('micro')) inferred.tiers = ['micro'];
  if (text.includes('macro')) inferred.tiers = ['macro'];
  if (text.includes('nano')) inferred.tiers = ['nano'];

  return inferred;
}

/** DiscoveryAiService::parseQuery's reply. */
interface ParsedQuery {
  q?: string | null;
  tiers?: string[];
  platform_ids?: number[];
  country?: string | null;
  content_types?: string[];
  engagement_min?: number | null;
  nl_label?: string;
}

/** The platform ids DiscoveryAiService's prompt gives the AI. */
const PARSED_PLATFORMS: Record<number, string[]> = {
  1: ['instagram'], 2: ['youtube'], 3: ['tiktok'], 4: ['facebook'], 5: ['twitter', 'x'],
};

/** Maps the AI's filters onto the rail's values (only values the rail offers). */
function fromParsedQuery(parsed: ParsedQuery, options: DiscoveryOptions): Partial<DiscoveryFilters> {
  const inferred: Partial<DiscoveryFilters> = {};
  const lower = (value: string) => value.toLowerCase();

  const tiers = (parsed.tiers ?? []).filter((tier): tier is CreatorTier => FOLLOWER_TIERS.some((t) => t.id === tier));
  if (tiers.length) inferred.tiers = tiers;

  const platformNames = (parsed.platform_ids ?? []).flatMap((id) => PARSED_PLATFORMS[id] ?? []);
  const platforms = options.platforms.filter((name) => platformNames.includes(lower(name)));
  if (platforms.length) inferred.platforms = platforms;

  const country = lower(parsed.country ?? '');
  const markets = country ? options.markets.filter((name) => country.includes(lower(name)) || lower(name).includes(country)) : [];
  if (markets.length) inferred.markets = markets;

  const types = (parsed.content_types ?? []).map(lower);
  const categories = options.categories.filter((name) =>
    types.some((type) => lower(name).includes(type) || type.includes(lower(name))),
  );
  if (categories.length) inferred.categories = categories;

  if (parsed.engagement_min && parsed.engagement_min > 0) inferred.minEngagement = parsed.engagement_min;
  return inferred;
}

/**
 * Discovery search state. Results come from `GET /discovery`, filtered
 * and sorted on the server like Laravel's Discovery ("Best fit" has no AI score
 * yet, so it lists newest first; "Lowest rate" sorts here, as rates are derived).
 */
export function useDiscovery() {
  const [filters, setFilters] = useState<DiscoveryFilters>(emptyFilters);
  const [sort, setSort] = useState<SortKey>('fit');
  const [found, setFound] = useState<CreatorProfile[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [options, setOptions] = useState<DiscoveryOptions>({ categories: [], platforms: [], markets: [] });

  useEffect(() => {
    api<DiscoveryOptions>('/discovery/options')
      .then(setOptions)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.categories.length) params.set('categories', filters.categories.join(','));
    if (filters.markets.length) params.set('markets', filters.markets.join(','));
    if (filters.platforms.length) params.set('platforms', filters.platforms.join(','));
    if (filters.tiers.length) params.set('tiers', filters.tiers.join(','));
    if (filters.minEngagement > 0) params.set('min_engagement', String(filters.minEngagement));
    if (sort === 'followers' || sort === 'engagement') params.set('sort', sort);

    let cancelled = false;
    setLoading(true);
    setError('');
    // Every filter change asks again at once; a reply to an older filter set is dropped.
    apiList<CreatorProfile>(`/discovery?${params}`)
      .then(({ data, meta }) => {
        if (cancelled) return;
        setFound(data);
        // The API returns up to 100; `total` is how many match overall.
        setTotal(meta.total);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, sort]);

  const toggleArrayValue = <T,>(key: keyof DiscoveryFilters, value: T) => {
    setFilters((prev) => {
      const current = prev[key] as unknown as T[];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [key]: next } as DiscoveryFilters;
    });
  };

  const results = useMemo(() => {
    if (sort !== 'rate') return found;
    // Lowest agreed rate first; creators without one go last.
    return [...found].sort((a, b) => (a.rate ?? Infinity) - (b.rate ?? Infinity));
  }, [found, sort]);

  const chips: FilterChip[] = useMemo(() => {
    const build: FilterChip[] = [];
    filters.categories.forEach((c) =>
      build.push({ id: `cat-${c}`, label: c, remove: () => toggleArrayValue('categories', c) }),
    );
    filters.markets.forEach((m) =>
      build.push({ id: `mkt-${m}`, label: m, remove: () => toggleArrayValue('markets', m) }),
    );
    filters.platforms.forEach((p) =>
      build.push({
        id: `plt-${p}`,
        label: p,
        remove: () => toggleArrayValue('platforms', p),
      }),
    );
    filters.tiers.forEach((t) =>
      build.push({ id: `tier-${t}`, label: `${t} tier`, remove: () => toggleArrayValue('tiers', t) }),
    );
    if (filters.minEngagement > 0) {
      build.push({
        id: 'eng',
        label: `${filters.minEngagement}%+ engagement`,
        remove: () => setFilters((prev) => ({ ...prev, minEngagement: 0 })),
      });
    }
    return build;
  }, [filters]);

  /**
   * Turns a typed brief into filters: the Twiva API's AI (POST /discovery/parse,
   * DiscoveryAiService) first, keyword matching when that isn't available.
   * Returns where the filters came from.
   */
  const applyPrompt = async (prompt: string): Promise<'ai' | 'keywords'> => {
    let inferred: Partial<DiscoveryFilters> = {};
    let source: 'ai' | 'keywords' = 'keywords';
    if (prompt.trim().length >= 5) {
      try {
        const { filters: parsed } = await api<{ filters: ParsedQuery }>('/discovery/parse', {
          method: 'POST',
          body: JSON.stringify({ query: prompt.trim().slice(0, 500) }),
        });
        inferred = fromParsedQuery(parsed ?? {}, options);
        if (Object.keys(inferred).length) source = 'ai';
      } catch {
        // Not on this API, or the AI failed: fall back to keywords.
      }
    }
    if (source === 'keywords') inferred = inferFilters(prompt, options.categories);
    setFilters((prev) => ({
      ...prev,
      ...inferred,
      categories: inferred.categories ?? prev.categories,
      markets: inferred.markets ?? prev.markets,
      platforms: inferred.platforms ?? prev.platforms,
      tiers: inferred.tiers ?? prev.tiers,
    }));
    return source;
  };

  const clearAll = () => setFilters(emptyFilters);

  const toggleCategory = (value: string) => toggleArrayValue('categories', value);
  const toggleMarket = (value: string) => toggleArrayValue('markets', value);
  const setMarkets = (value: string[]) => setFilters((prev) => ({ ...prev, markets: value }));
  const togglePlatform = (value: string) => toggleArrayValue('platforms', value);
  const toggleTier = (value: CreatorTier) => toggleArrayValue('tiers', value);
  const setMinEngagement = (value: number) =>
    setFilters((prev) => ({ ...prev, minEngagement: value }));

  return {
    filters,
    sort,
    setSort,
    results,
    chips,
    loading,
    error,
    total,
    options,
    applyPrompt,
    clearAll,
    toggleCategory,
    toggleMarket,
    setMarkets,
    togglePlatform,
    toggleTier,
    setMinEngagement,
  };
}